<script>
import ConfigSheet from "./ConfigSheet";
import { loadStoredConfig, saveStoredConfig } from "./storedConfig";

const JOB_INFO_CONFIG_KEY = "web-cab:jobInfoConfig";

const OPTION_FIELD_COUNT = 9;

const DEFAULT_JOB_INFO_CONFIG = {
  visibleFields: {
    workorder: true,
    clientName: true,
    phone: true,
    address: true,
    deliveryDate: true,
    installationDate: true,
    productionDate: true,
    types: true,
    state: true,
    ...Object.fromEntries(
      Array.from({ length: OPTION_FIELD_COUNT }, (_, i) => [
        "option" + (i + 1),
        true,
      ]),
    ),
  },
};

export default {
  components: { ConfigSheet },
  props: {
    nbBO: { type: Number, default: 0 },
    nbDamaged: { type: Number, default: 0 },
    nbProduct: { type: Number, default: 0 },
    nbDetached: { type: Number, default: 0 },
    availablePackagings: { type: Array, default: () => [] },
    packingFilter: { type: Object, default: null },
    workOrderInfo: { type: Object, default: null },
  },
  data() {
    return {
      jobInfoConfig: loadStoredConfig(
        JOB_INFO_CONFIG_KEY,
        DEFAULT_JOB_INFO_CONFIG,
      ),
    };
  },
  watch: {
    jobInfoConfig: {
      deep: true,
      handler(val) {
        saveStoredConfig(JOB_INFO_CONFIG_KEY, val);
      },
    },
  },
  computed: {
    deliveryDate() {
      return this.formatDate(this.workOrderInfo?.deliveryDate);
    },
    installationDate() {
      return this.formatDate(this.workOrderInfo?.installationDate);
    },
    productionDate() {
      return this.formatDate(this.workOrderInfo?.productionDate);
    },
    configFields() {
      return [
        { key: "workorder", label: this.$t("general.workorder") },
        { key: "clientName", label: this.$t("general.clientname") },
        { key: "phone", label: this.$t("general.phone") },
        { key: "address", label: this.$t("general.address") },
        { key: "deliveryDate", label: this.$t("general.deliverydate") },
        { key: "installationDate", label: this.$t("general.installationdate") },
        { key: "productionDate", label: this.$t("general.productiondate") },
        { key: "types", label: this.$t("general.types") },
        { key: "state", label: this.$t("general.state") },
        ...Array.from({ length: OPTION_FIELD_COUNT }, (_, i) => ({
          key: "option" + (i + 1),
          label: `${this.$t("general.option")} ${i + 1}`,
        })),
      ];
    },
    configSections() {
      return [
        {
          title: this.$t("general.visiblefields"),
          fields: this.configFields,
        },
      ];
    },
    optionRows() {
      const wo = this.workOrderInfo;
      if (!wo) return [];
      return Array.from({ length: OPTION_FIELD_COUNT }, (_, i) => ({
        key: "option" + (i + 1),
        label: `${this.$t("general.option")} ${i + 1}`,
        value: (wo["option" + (i + 1)] || "").trim(),
      })).filter((r) => r.value && this.fieldVisible(r.key));
    },
    clientName() {
      return this.workOrderInfo?.clientName?.trim() || null;
    },
    clientPhone() {
      const raw = this.workOrderInfo?.clientPhone;
      if (!raw) return null;
      const cleaned = ("" + raw).replace(/\D/g, "");
      const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        const intlCode = match[1] ? "+1 " : "";
        return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
      }
      return raw.trim() || null;
    },
    addressLines() {
      const wo = this.workOrderInfo;
      if (!wo) return [];
      const cleanLine = (s) =>
        typeof s === "string" ? s.replace(/[\r\n]+/g, " ").trim() : "";
      const lines = [];
      const a1 = cleanLine(wo.clientAddress);
      if (a1) lines.push(a1);
      const a2 = cleanLine(wo.clientAddressLine2);
      if (a2) lines.push(a2);
      const cityStateZip = [wo.clientCity, wo.clientState, wo.clientZipCode]
        .map(cleanLine)
        .filter(Boolean)
        .join(" ");
      if (cityStateZip) lines.push(cityStateZip);
      return lines;
    },
  },
  methods: {
    formatDate(d) {
      if (!d) return null;
      const date = new Date(d);
      if (isNaN(date.getTime()) || date.getFullYear() < 1900) return null;
      return this.$dayjs(d + "Z").format("YYYY-MM-DD");
    },
    selectPacking(option) {
      this.$emit("select-packing", option);
    },
    fieldVisible(key) {
      return this.jobInfoConfig?.visibleFields?.[key] !== false;
    },
  },
};
</script>
<template>
  <div class="tab-section">
    <div class="info-card">
      <div class="info-head">
        <i class="mdi mdi-briefcase-outline"></i>
        <span>{{ $t("general.information") }}</span>
        <span class="info-head-cog">
          <ConfigSheet
            v-model="jobInfoConfig"
            :sections="configSections"
            small
          />
        </span>
      </div>
      <div class="info-row" v-if="workOrderInfo && fieldVisible('workorder')">
        <span class="info-lbl">{{ $t("general.workorder") }}</span>
        <span class="info-val">{{ workOrderInfo.name }}</span>
      </div>
      <div class="info-row" v-if="clientName && fieldVisible('clientName')">
        <span class="info-lbl">{{ $t("general.clientname") }}</span>
        <span class="info-val">{{ clientName }}</span>
      </div>
      <div class="info-row" v-if="clientPhone && fieldVisible('phone')">
        <span class="info-lbl">{{ $t("general.phone") }}</span>
        <span class="info-val">{{ clientPhone }}</span>
      </div>
      <div
        class="info-row info-row-wrap"
        v-if="addressLines.length && fieldVisible('address')"
      >
        <span class="info-lbl">{{ $t("general.address") }}</span>
        <div class="info-val">
          <div v-for="(line, idx) in addressLines" :key="idx">{{ line }}</div>
        </div>
      </div>
      <div class="info-row" v-if="fieldVisible('deliveryDate')">
        <span class="info-lbl">{{ $t("general.deliverydate") }}</span>
        <span class="info-val" :class="{ 'text-muted': !deliveryDate }">{{
          deliveryDate || "—"
        }}</span>
      </div>
      <div class="info-row" v-if="fieldVisible('installationDate')">
        <span class="info-lbl">{{ $t("general.installationdate") }}</span>
        <span class="info-val" :class="{ 'text-muted': !installationDate }">{{
          installationDate || "—"
        }}</span>
      </div>
      <div class="info-row" v-if="fieldVisible('productionDate')">
        <span class="info-lbl">{{ $t("general.productiondate") }}</span>
        <span class="info-val" :class="{ 'text-muted': !productionDate }">{{
          productionDate || "—"
        }}</span>
      </div>
      <div class="info-row" v-if="fieldVisible('types')">
        <span class="info-lbl">{{ $t("general.types") }}</span>
        <div class="info-badges">
          <span class="badge-product" v-if="nbProduct > 0">{{ $t("general.products") }} ({{ nbProduct }})</span>
          <span class="badge-acc" v-if="nbDetached > 0">{{ $t("general.accessories") }} ({{ nbDetached }})</span>
        </div>
      </div>
      <div class="info-row" v-if="fieldVisible('state')">
        <span class="info-lbl">{{ $t("general.state") }}</span>
        <div class="info-badges">
          <span v-if="nbBO > 0" class="badge-bo">{{ $t("general.bo") }} ({{ nbBO }})</span>
          <span v-if="nbDamaged > 0" class="badge-dmg">{{ $t("general.damaged") }} ({{ nbDamaged }})</span>
          <span v-if="nbBO === 0 && nbDamaged === 0" class="badge-ok">{{ $t("general.ok") }}</span>

        </div>
      </div>
      <div class="info-row" v-for="row in optionRows" :key="row.key">
        <span class="info-lbl">{{ row.label }}</span>
        <span class="info-val">{{ row.value }}</span>
      </div>
    </div>
  </div>
</template>
<style scoped>
.tab-section {
  display: flex;
  flex-direction: column;
  gap: 9px;
  max-height: 100%;
  overflow: auto;
}
.info-card {
  background: white;
  border-radius: 11px;
  border: 1px solid #e0e5ea;
}
.info-head {
  background: #f1b44c;
  color: white;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}
.info-head-cog {
  margin-left: auto;
  display: flex;
  align-items: center;
}
.info-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e0e5ea;
  flex-wrap: nowrap;
  gap: 8px;
}
.info-row:last-child {
  border-bottom: none;
}
.info-row-wrap {
  flex-wrap: wrap;
}
.info-lbl {
  font-size: 11px;
  color: #7a8a99;
  min-width: 105px;
}
.info-val {
  font-size: 12px;
  color: #1c2e3e;
  font-weight: 500;
}
.info-badges {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}
.badge-product {
  background: #1c2e3e;
  color: white;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 500;
}
.badge-ok {
  background: #34c38f;
  color: white;
  border-radius: 9px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 500;
}
.badge-acc {
  background: #f0f4f7;
  color: #7a8a99;
  border: 1px solid #e0e5ea;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 500;
}
.badge-bo {
  background: #fdecea;
  color: #e74c3c;
  border: 1px solid #f9bdbd;
  border-radius: 9px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 500;
}
.badge-dmg {
  background: #fff8e1;
  color: #b07d00;
  border: 1px solid #f9d77e;
  border-radius: 9px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 500;
}
.pack-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.pack-chip {
  background: #f4f6f8;
  border: 1px solid #e0e5ea;
  border-radius: 7px;
  padding: 2px 9px;
  font-size: 11px;
  cursor: pointer;
  color: #1c2e3e;
}
.pack-chip.active {
  background: #f1b44c;
  color: white;
  border-color: #f1b44c;
}
</style>
